import { Component } from "@theme/component";
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  DiscountUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";
import {
  GtmEvents,
  pushGtmEvent,
  mapShopifyCartItems,
} from "@theme/gtm-tracking";

/** @typedef {import('@theme/events').QuantitySelectorUpdateEvent} QuantitySelectorUpdateEvent */
/** @typedef {import('@theme/events').CartAddEvent} CartAddEvent */
/** @typedef {import('./utilities').TextComponent} TextComponent */
/** @typedef {import('@theme/gtm-tracking').ShopifyCartItem} ShopifyCartItem */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate,
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate,
    );
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    // Resolve the row from the dispatching selector itself (bulletproof vs.
    // positional drift); reused for the mutation key and the shimmer target.
    const row = this.#rowFromTrigger(event, line);

    if (quantity === 0) {
      return this.onLineItemRemove(line, row);
    }

    this.updateQuantity({
      line,
      quantity,
      action: "change",
      row,
    });

    if (!row) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      row.querySelector("text-component")
    );
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   * @param {unknown} [trigger] - The click Event from the remove button (or an
   *   already-resolved row element); used to target the exact clicked line.
   */
  onLineItemRemove(line, trigger) {
    const cartItemRowToRemove = this.#rowFromTrigger(trigger, line);

    this.updateQuantity({
      line,
      quantity: 0,
      action: "clear",
      row: cartItemRowToRemove,
    });

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter(
        (row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key,
      ),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById("empty-cart-template");
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? "empty-cart-drawer" : "empty-cart-page"]);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty("--row-height", `${row.clientHeight}px`);
      row.classList.add("removing");

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Resolves the rendered quantity input for a true cart line index via its
   * `data-cart-line` attribute. Correct even when gift/sample lines hidden at
   * qty <= 1 (see cart-products.liquid) compact the rendered rows out of
   * positional alignment — unlike `refs.quantitySelectors[line - 1]`, which
   * would point at the wrong row when a hidden line precedes this one. SHOP-1314.
   * @param {number} line
   * @returns {HTMLInputElement | null}
   */
  #inputForLine(line) {
    const input = this.querySelector(`input[data-cart-line="${line}"]`);
    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * Resolves the cart line row (its `[data-key]` element) for a true cart line
   * index. See {@link #inputForLine}.
   * @param {number} line
   * @returns {HTMLElement | null}
   */
  #rowForLine(line) {
    const row = this.#inputForLine(line)?.closest("[data-key]");
    return row instanceof HTMLElement ? row : null;
  }

  /**
   * Resolves the cart line row (its `[data-key]` element) from whatever
   * triggered the action — the clicked remove button or the quantity selector
   * that dispatched the event (both live inside the row). This is the most
   * robust path: it can't drift out of alignment the way a positional `line`
   * lookup can when the promo app mutates cart composition behind our back.
   * Falls back to {@link #rowForLine} when no usable trigger is supplied.
   * @param {unknown} trigger - The triggering Event, the row element, or null.
   * @param {number} line
   * @returns {HTMLElement | null}
   */
  #rowFromTrigger(trigger, line) {
    const target =
      trigger instanceof Element
        ? trigger
        : trigger &&
            typeof trigger === "object" &&
            "target" in trigger &&
            /** @type {{ target: unknown }} */ (trigger).target instanceof
              Element
          ? /** @type {Element} */ (
              /** @type {{ target: Element }} */ (trigger).target
            )
          : null;
    const row = target?.closest("[data-key]") ?? this.#rowForLine(line);
    return row instanceof HTMLElement ? row : null;
  }

  /**
   * Re-renders this cart section from server truth, logging (not throwing on) a
   * failed resync so it can't surface as an unhandled promise rejection.
   */
  #resyncSection() {
    sectionRenderer
      .renderSection(this.sectionId, { cache: false })
      .catch((error) => {
        console.error("[cart-items] section resync failed", error);
      });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {HTMLElement | null} [config.row] - The line's `[data-key]` row,
   *   resolved from the triggering event. Preferred over a by-line lookup.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`,
    );

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    // Capture pre-update line state for tracking before the fetch mutates it.
    // Prefer the row resolved from the triggering event (config.row); fall back
    // to a by-line lookup. Every derived value (key, variant, previous qty) is
    // read from that one row so it stays tied to the line the customer touched,
    // even when gift/sample lines hidden at qty <= 1 shift positional indices.
    const row =
      config.row instanceof HTMLElement ? config.row : this.#rowForLine(line);
    const cartQuantityInput =
      /** @type {HTMLInputElement | null} */ (
        row?.querySelector("input[data-cart-line]")
      ) ?? this.#inputForLine(line);
    const previousQuantity = parseInt(
      cartQuantityInput?.dataset.cartQuantity || "0",
      10,
    );
    const variantId =
      cartQuantityInput
        ?.closest("[data-variant-id]")
        ?.getAttribute("data-variant-id") ?? undefined;

    // SHOP-1314: address the line by its stable Shopify line key (the row's
    // `data-key`), not its 1-based position. The promo app prepends/removes GWP
    // + sample lines via raw /cart/* calls the theme never re-renders for, so a
    // positional `line` can point at the wrong line — e.g. a quantity change
    // landing on the gift line instead of the item the customer clicked. The
    // key is unambiguous even when two lines share a variant (gift vs. regular
    // purchase). Falls back to the positional index only if the line isn't
    // rendered (then `line` is still the correct true index server-side).
    const lineKey = row?.dataset.key;

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      // Prefer the stable line key; fall back to the positional index only if
      // the key is somehow absent (e.g. a detached node or older render).
      ...(lineKey ? { id: lineKey } : { line }),
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig("json", { body }))
      .then((response) =>
        response.text().then((responseText) => ({
          ok: response.ok,
          responseText,
        })),
      )
      .then(({ ok, responseText }) => {
        resetShimmer(this);

        let parsedResponseText;
        try {
          parsedResponseText = JSON.parse(responseText);
        } catch {
          // Non-JSON body (e.g. a 429 bot-challenge or 5xx HTML edge page).
          // Don't let the SyntaxError fall into the catch and leave the cart
          // shimmering with a stale quantity — resync from server truth.
          console.warn(
            `[cart-items] non-JSON cart response for line ${line}; resyncing section.`,
          );
          this.#resyncSection();
          return;
        }

        // Shopify signals a failed change two ways: a 422 carrying an `errors`
        // field (quantity / inventory rules) or a 4xx carrying `{ status,
        // message, description }` with no `errors` and no `sections` — e.g. a
        // line key that no longer exists because the promo app removed it.
        // Both are non-OK, so gate on `ok` and route them through the error
        // handler rather than falling through to the success path (which would
        // break on the missing `sections`). SHOP-1314.
        if (!ok || parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        this.#pushCartAdjustmentEvent(
          config.action,
          variantId,
          quantity,
          previousQuantity,
          parsedResponseText,
        );

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          "text/html",
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector(
          '[ref="cartItemCount"]',
        )?.textContent;
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: "cart-items-component",
            sections: parsedResponseText.sections,
          }),
        );

        morphSection(
          this.sectionId,
          parsedResponseText.sections[this.sectionId],
          this.isDrawer ? "hydration" : "full",
        );

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        // Unexpected failure (network rejection, or a throw in the success-path
        // DOM work). Log it and resync so the displayed quantity can't stay out
        // of step with the server.
        console.error("[cart-items] updateQuantity failed", error);
        this.#resyncSection();
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Pushes a `cartUpdateQuantity` or `cartRemove` dataLayer event with fresh cart contents.
   * @param {string} action - The updateQuantity action ("change" or "clear").
   * @param {string | undefined} variantId - The variant id of the changed line.
   * @param {number} newQuantity - The new quantity for the line.
   * @param {number} previousQuantity - The previous (server-confirmed) quantity for the line.
   * @param {{items?: ShopifyCartItem[], total_price?: number, currency?: string}} cart - The cart response payload.
   */
  #pushCartAdjustmentEvent(
    action,
    variantId,
    newQuantity,
    previousQuantity,
    cart,
  ) {
    pushGtmEvent(
      action === "clear"
        ? GtmEvents.CART_REMOVE
        : GtmEvents.CART_UPDATE_QUANTITY,
      {
        eventData: {
          product_id: variantId,
          newQuantity,
          previousQuantity,
        },
        cart_drawer: {
          items: mapShopifyCartItems(cart.items),
          cart_total:
            cart.total_price != null ? cart.total_price / 100 : undefined,
          currency: cart.currency,
        },
      },
    );
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {object} parsedResponseText - The parsed cart error response.
   * @param {string} [parsedResponseText.errors] - Quantity/inventory rule error.
   * @param {string} [parsedResponseText.description] - Error description (e.g. a removed line key: "Cannot find variant").
   * @param {string} [parsedResponseText.message] - Error message.
   */
  #handleCartError = (line, parsedResponseText) => {
    // Shopify errors arrive as `errors` (quantity rules) or `description` /
    // `message` (e.g. "Cannot find variant" for a removed line key). Surface
    // whichever is present.
    const message =
      parsedResponseText?.errors ||
      parsedResponseText?.description ||
      parsedResponseText?.message ||
      "";

    const quantityInput = this.#inputForLine(line);

    // Reset the input to its server-confirmed value when we can find it.
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    // Surface the error inline when the matching error UI is present.
    if (
      cartItemError instanceof HTMLElement &&
      cartItemErrorContainer instanceof HTMLElement
    ) {
      cartItemError.textContent = message;
      cartItemErrorContainer.classList.remove("hidden");
      return;
    }

    // SHOP-1314: never throw from the expected error path. If the DOM the theme
    // rendered no longer matches the cart (e.g. the promo app mutated line
    // composition behind our back, so `line` is out of range), the input and
    // error containers won't be found. Rather than throwing an uncaught error
    // that gets swallowed and leaves the change silently failed, re-render from
    // server truth so the cart resyncs.
    console.warn(
      `[cart-items] cart error for line ${line} but its DOM is stale; resyncing section.`,
      message,
    );
    this.#resyncSection();
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"]`,
      );

      for (const selector of selectors) {
        const input = selector.querySelector("input[data-cart-quantity]");
        if (!input) continue;

        input.setAttribute("data-cart-quantity", item.quantity.toString());

        // Update the quantity selector's internal state
        if (
          "updateCartQuantity" in selector &&
          typeof selector.updateCartQuantity === "function"
        ) {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll(
      "cart-quantity-selector-component",
    )) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
